/**
 * AddSupplierPanel — Joan's "add a supplier" tool.
 *
 * Lets a reviewer add a supplier to the GLOBAL supplier dictionary (cross-client
 * reference data), so BLIP tracks it in future benchmark runs. This is a
 * curation action, not a per-run edit — it does not change the current report's
 * (immutable) numbers; it takes effect from the next run/re-aggregation.
 *
 * Hybrid gate (server-enforced): a COMPLETE entry — a supplier type plus at
 * least one website domain — goes live immediately; an under-specified one is
 * queued for review so a thin guess can't pollute the shared dictionary. A name
 * already tracked is amended; a deny-listed name is blocked.
 *
 * Durability: entries added here are tagged source='manual', so the periodic
 * dictionary rebuild preserves them and the export CLI can fold them into the
 * codebase — they are never silently wiped, and they sync back to localhost/git.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  BulkAddSuppliersResult,
  ManualSupplierEntry,
  SupplierType,
} from '@aurora/shared';
import { api, ApiClientError } from '../api/client';

const TYPE_LABELS: Record<Exclude<SupplierType, 'unknown'>, string> = {
  manufacturer: 'Manufacturer / maker',
  distributor: 'Distributor / reseller',
  supplier_directory: 'Supplier directory',
  services_marketplace: 'Services marketplace',
};

/** Split a comma/newline-separated field into trimmed, non-empty values. */
function splitList(raw: string): string[] {
  return raw
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function AddSupplierPanel(): JSX.Element {
  const [name, setName] = useState('');
  const [type, setType] = useState<SupplierType>('manufacturer');
  const [domains, setDomains] = useState('');
  const [aliases, setAliases] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [added, setAdded] = useState<ManualSupplierEntry[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] =
    useState<BulkAddSuppliersResult | null>(null);

  const loadAdded = useCallback(async (): Promise<void> => {
    try {
      const r = await api.listManualSuppliers();
      setAdded(r.suppliers);
    } catch {
      /* non-fatal — the list just stays as-is */
    }
  }, []);
  useEffect(() => {
    void loadAdded();
  }, [loadAdded]);

  const willGoLive = name.trim().length >= 2 && splitList(domains).length > 0;

  const submit = async (): Promise<void> => {
    if (name.trim().length < 2 || busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api.addSupplier({
        canonicalName: name.trim(),
        domains: splitList(domains),
        aliases: splitList(aliases),
        supplierType: type,
        note: note.trim() || undefined,
      });
      if (r.mode === 'blocked') {
        setError(r.message);
      } else {
        setNotice(r.message);
        // Clear the entry fields (keep the chosen type for repeat adds).
        setName('');
        setDomains('');
        setAliases('');
        setNote('');
        await loadAdded();
      }
    } catch (e) {
      setError(
        e instanceof ApiClientError ? e.message : 'Failed to add supplier',
      );
    } finally {
      setBusy(false);
    }
  };

  const runImport = async (file: File): Promise<void> => {
    setImporting(true);
    setError(null);
    setNotice(null);
    setImportSummary(null);
    try {
      const r = await api.importSuppliers(file);
      setImportSummary(r);
      await loadAdded();
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Import failed');
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <details className="card add-supplier-panel">
      <summary>Add a supplier</summary>
      <p className="muted">
        Add a supplier to the tracked dictionary so it’s picked up in future
        runs. Give a <strong>website</strong> and a <strong>type</strong> and it
        goes live right away; with just a name it’s queued for review. This
        updates the shared dictionary for <strong>all</strong> reports — it
        doesn’t change this report’s existing numbers (re-run to see it
        counted).
      </p>

      <label className="field">
        <span>Supplier name</span>
        <input
          type="text"
          value={name}
          placeholder="e.g. Proteintech  —  or  Gibco / Thermo Fisher Scientific"
          onChange={(e) => {
            setName(e.target.value);
            setNotice(null);
            setError(null);
          }}
          disabled={busy}
        />
      </label>

      <label className="field">
        <span>Type</span>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as SupplierType)}
          disabled={busy}
        >
          {(Object.keys(TYPE_LABELS) as Array<keyof typeof TYPE_LABELS>).map(
            (t) => (
              <option key={t} value={t}>
                {TYPE_LABELS[t]}
              </option>
            ),
          )}
        </select>
      </label>

      <label className="field">
        <span>Website domain(s)</span>
        <input
          type="text"
          value={domains}
          placeholder="proteintech.com  (comma-separate several)"
          onChange={(e) => {
            setDomains(e.target.value);
            setNotice(null);
          }}
          disabled={busy}
        />
      </label>

      <label className="field">
        <span>
          Other names / sub-brands <em className="muted">(optional)</em>
        </span>
        <input
          type="text"
          value={aliases}
          placeholder="comma-separated aliases that should fold into this supplier"
          onChange={(e) => setAliases(e.target.value)}
          disabled={busy}
        />
      </label>

      <label className="field">
        <span>
          Note <em className="muted">(optional)</em>
        </span>
        <input
          type="text"
          value={note}
          placeholder="why you’re adding it — kept for the record"
          onChange={(e) => setNote(e.target.value)}
          disabled={busy}
        />
      </label>

      {error && <div className="form-error">{error}</div>}
      {notice && <div className="form-notice">{notice}</div>}

      <div className="head-actions" style={{ marginTop: '0.5rem' }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void submit()}
          disabled={busy || name.trim().length < 2}
        >
          {busy
            ? 'Adding…'
            : willGoLive
              ? 'Add supplier'
              : 'Queue supplier for review'}
        </button>
      </div>

      <h4 className="layout-subhead">Or import a list</h4>
      <p className="muted">
        Upload a CSV, Excel (.xlsx), Word (.docx), or text (.txt) file of
        suppliers. Rows with a website go live; name-only rows are queued for
        review. Helpful columns: <em>name</em>, <em>website</em>, <em>type</em>,{' '}
        <em>other names</em>.
      </p>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,.xlsx,.xls,.docx,.txt"
        disabled={importing}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void runImport(f);
        }}
      />
      {importing && <p className="muted">Importing…</p>}
      {importSummary && (
        <div className="form-notice">
          Imported {importSummary.total}: {importSummary.added} added,{' '}
          {importSummary.amended} updated, {importSummary.queued} queued for
          review
          {importSummary.blocked > 0
            ? `, ${importSummary.blocked} skipped`
            : ''}
          .
          {importSummary.notes.map((n, i) => (
            <div key={i} className="muted">
              {n}
            </div>
          ))}
        </div>
      )}

      {added.length > 0 && (
        <>
          <h4 className="layout-subhead">Your added suppliers</h4>
          <ul className="feedback-history">
            {added.map((s) => (
              <li key={s.id}>
                <span className="pill pill-complete">{s.supplierType}</span>{' '}
                {s.canonicalName}
                {s.domains.length > 0 && (
                  <em className="muted"> — {s.domains.join(', ')}</em>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </details>
  );
}
